import { useCallback, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import { toast } from "sonner";
import { useTranslation } from "@/i18n";
import { AutosaveStatusPill } from "@/components/shared/autosave-status-pill";
import { useSaveStatus } from "@/hooks/use-save-status";
import { TimezoneSelect } from "@/components/shared/timezone-select";
import { useMyWorkHours } from "@/hooks/use-my-work-hours";
import { useStore } from "@/stores/store-context";
import { cn } from "@/lib/utils";
import type { TWeekdayKey } from "@/types/user";

const WEEKDAYS: TWeekdayKey[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const MIN_HOURS = 0;
const MAX_HOURS = 24;
const MIN_CAPACITY_PERCENT = 0;
const MAX_CAPACITY_PERCENT = 100;

const ROW_CLASSES = "flex items-center justify-between rounded-md px-3 py-2 odd:bg-muted/40";

function clampHours(value: string): number | null {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return null;
  if (parsed < MIN_HOURS || parsed > MAX_HOURS) return null;
  return Math.round(parsed * 100) / 100;
}

function clampCapacityPercent(value: string): number | null {
  const parsed = Number.parseFloat(value);
  if (!Number.isFinite(parsed)) return null;
  if (parsed < MIN_CAPACITY_PERCENT || parsed > MAX_CAPACITY_PERCENT) return null;
  return Math.round(parsed * 100) / 100;
}

function formatCapacityPercent(value: number): string {
  return Number.isInteger(value) ? value.toFixed(0) : value.toFixed(2);
}

const DayRow = observer(function DayRow({
  day,
  label,
  value,
  onSave,
  invalidMessage,
}: {
  day: TWeekdayKey;
  label: string;
  value: number;
  onSave: (value: number) => Promise<void>;
  invalidMessage: string;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const startEditing = useCallback(() => {
    setEditValue(value.toFixed(2));
    setIsEditing(true);
  }, [value]);

  const commit = useCallback(async () => {
    setIsEditing(false);
    const parsed = clampHours(editValue);
    if (parsed === null) {
      toast.error(invalidMessage);
      return;
    }
    if (parsed === value) return;
    await onSave(parsed);
  }, [editValue, invalidMessage, onSave, value]);

  return (
    <div className={ROW_CLASSES}>
      <span className="text-sm font-medium text-foreground">{label}</span>
      {isEditing ? (
        <input
          ref={inputRef}
          id={`work-hours-${day}`}
          aria-label={label}
          type="number"
          min={MIN_HOURS}
          max={MAX_HOURS}
          step={0.5}
          value={editValue}
          onChange={(event) => setEditValue(event.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void commit();
            } else if (event.key === "Escape") {
              setIsEditing(false);
            }
          }}
          className="w-20 rounded-sm border border-input bg-background px-2 py-1 text-right text-sm font-medium focus:border-ring focus:outline-none"
        />
      ) : (
        <button
          type="button"
          aria-label={label}
          onClick={startEditing}
          className="w-20 cursor-pointer rounded-sm px-2 py-1 text-right text-sm font-medium text-foreground hover:bg-muted"
        >
          {value.toFixed(2)}h
        </button>
      )}
    </div>
  );
});

const CapacityRow = observer(function CapacityRow({
  value,
  onSave,
  invalidMessage,
}: {
  value: number;
  onSave: (value: number) => Promise<void>;
  invalidMessage: string;
}) {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const startEditing = useCallback(() => {
    setEditValue(value.toFixed(2));
    setIsEditing(true);
  }, [value]);

  const commit = useCallback(async () => {
    setIsEditing(false);
    const parsed = clampCapacityPercent(editValue);
    if (parsed === null) {
      toast.error(invalidMessage);
      return;
    }
    if (parsed === value) return;
    await onSave(parsed);
  }, [editValue, invalidMessage, onSave, value]);

  const label = t("profile_settings.work_hours.capacity_label");

  return (
    <div className={ROW_CLASSES}>
      <span className="text-sm font-medium text-foreground">{label}</span>
      {isEditing ? (
        <input
          ref={inputRef}
          id="work-hours-capacity"
          aria-label={label}
          type="number"
          min={MIN_CAPACITY_PERCENT}
          max={MAX_CAPACITY_PERCENT}
          step={5}
          value={editValue}
          onChange={(event) => setEditValue(event.target.value)}
          onBlur={() => void commit()}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              void commit();
            } else if (event.key === "Escape") {
              setIsEditing(false);
            }
          }}
          className="w-20 rounded-sm border border-input bg-background px-2 py-1 text-right text-sm font-medium focus:border-ring focus:outline-none"
        />
      ) : (
        <button
          type="button"
          aria-label={label}
          onClick={startEditing}
          className="w-20 cursor-pointer rounded-sm px-2 py-1 text-right text-sm font-medium text-foreground hover:bg-muted"
        >
          {formatCapacityPercent(value)}%
        </button>
      )}
    </div>
  );
});

export const WorkHoursForm = observer(function WorkHoursForm() {
  const { t } = useTranslation();
  const { workHours: store } = useStore();
  const { workHours: data, loadFailed } = useMyWorkHours();

  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("17:00");
  const [timezone, setTimezone] = useState("UTC");
  const [timeError, setTimeError] = useState<string | null>(null);
  // The time zone saves on pick and shows the pick at once, so a status says
  // whether it was stored (NG-2803): Saving…, a brief Saved, a persistent Save
  // failed, and at rest a screen-reader "Up to date" only a confirmed write
  // produces.
  const timezoneSave = useSaveStatus();

  useEffect(() => {
    // Keyed on the failure alone: the toast is for it, not for a language change.
    if (loadFailed) toast.error(t("profile_settings.work_hours.load_error"));
  }, [loadFailed]);

  useEffect(() => {
    if (!data) return;
    setStartTime(data.work_start_time);
    setEndTime(data.work_end_time);
    setTimezone(data.timezone);
  }, [data]);

  const saveDay = async (day: TWeekdayKey, value: number) => {
    try {
      await store.updateWorkHours({ [day]: value });
    } catch (error) {
      console.error("[work-hours] day save failed", { day, error });
      toast.error(t("profile_settings.work_hours.save_error"));
    }
  };

  const saveCapacityPercent = async (value: number) => {
    try {
      await store.updateWorkHours({ capacity_percent: value });
    } catch (error) {
      console.error("[work-hours] capacity save failed", { error });
      toast.error(t("profile_settings.work_hours.save_error"));
    }
  };

  const commitTimes = async (nextStart: string, nextEnd: string) => {
    if (nextStart >= nextEnd) {
      setTimeError(t("profile_settings.work_hours.end_before_start"));
      return;
    }
    setTimeError(null);
    if (data && data.work_start_time === nextStart && data.work_end_time === nextEnd) return;
    try {
      await store.updateWorkHours({ work_start_time: nextStart, work_end_time: nextEnd });
    } catch (error) {
      console.error("[work-hours] times save failed", { error });
      toast.error(t("profile_settings.work_hours.save_error"));
    }
  };

  const commitTimezone = async (next: string) => {
    setTimezone(next);
    if (data && data.timezone === next) return;
    timezoneSave.begin();
    try {
      await store.updateWorkHours({ timezone: next });
      timezoneSave.succeed();
    } catch (error) {
      console.error("[work-hours] timezone save failed", { timezone: next, error });
      timezoneSave.fail();
      toast.error(t("profile_settings.work_hours.save_error"));
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <section className="flex flex-col gap-1">
        <h4 className="mb-1 text-sm font-medium text-foreground">
          {t("profile_settings.work_hours.capacity_section_label")}
        </h4>
        <CapacityRow
          value={data?.capacity_percent ?? 100}
          onSave={saveCapacityPercent}
          invalidMessage={t("profile_settings.work_hours.invalid_capacity")}
        />
      </section>

      <section className="flex flex-col gap-1">
        <h4 className="mb-1 text-sm font-medium text-foreground">
          {t("profile_settings.work_hours.daily_hours_label")}
        </h4>
        {WEEKDAYS.map((day) => (
          <DayRow
            key={day}
            day={day}
            label={t(`profile_settings.work_hours.day_${day}`)}
            value={data?.[day] ?? 0}
            onSave={(value) => saveDay(day, value)}
            invalidMessage={t("profile_settings.work_hours.invalid_hours")}
          />
        ))}
      </section>

      <section className="flex flex-col gap-1">
        <h4 className="mb-1 text-sm font-medium text-foreground">
          {t("profile_settings.work_hours.work_schedule_label")}
        </h4>
        <div className={ROW_CLASSES}>
          <label htmlFor="work-hours-start" className="text-sm font-medium text-foreground">
            {t("profile_settings.work_hours.start_time_label")}
          </label>
          <input
            id="work-hours-start"
            type="time"
            value={startTime}
            onChange={(event) => setStartTime(event.target.value)}
            onBlur={() => void commitTimes(startTime, endTime)}
            className="rounded-sm border border-input bg-background px-2 py-1 text-sm font-medium focus:border-ring focus:outline-none"
          />
        </div>
        <div className={ROW_CLASSES}>
          <label htmlFor="work-hours-end" className="text-sm font-medium text-foreground">
            {t("profile_settings.work_hours.end_time_label")}
          </label>
          <input
            id="work-hours-end"
            type="time"
            value={endTime}
            onChange={(event) => setEndTime(event.target.value)}
            onBlur={() => void commitTimes(startTime, endTime)}
            className="rounded-sm border border-input bg-background px-2 py-1 text-sm font-medium focus:border-ring focus:outline-none"
          />
        </div>
        <div className={cn(ROW_CLASSES, "gap-3")}>
          <label htmlFor="work-hours-timezone" className="text-sm font-medium text-foreground">
            {t("profile_settings.work_hours.timezone_label")}
          </label>
          {timezoneSave.touched ? (
            <AutosaveStatusPill
              {...timezoneSave.pillProps}
              className="ml-auto min-w-0"
              hideAtRest
            />
          ) : null}
          <TimezoneSelect
            id="work-hours-timezone"
            value={timezone}
            onChange={(next) => void commitTimezone(next)}
            className="mt-0 w-[260px]"
          />
        </div>
        {timeError ? <p className="px-3 text-xs text-destructive">{timeError}</p> : null}
      </section>
    </div>
  );
});
